import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './ui';
import { IDMUser, listIdmUsers } from '../lib/agentsApi';
import { useTranslation } from 'react-i18next';

interface IDMUserSelectProps {
  apiUrl: string;
  value: string | null;
  onChange: (userId: string | null, user: IDMUser | null) => void;
}

/** Friendly label for the identity source powering the owner picker. */
const SOURCE_LABEL: Record<string, string> = {
  entra_id: 'Microsoft Entra ID',
  entra: 'Microsoft Entra ID',
  azure_ad: 'Microsoft Entra ID',
  aad: 'Microsoft Entra ID',
  active_directory: 'Active Directory',
  ad: 'Active Directory',
  okta: 'Okta',
  keycloak: 'Keycloak',
  local: 'Local users (demo)',
};

/**
 * Owner picker over the human identity directory. When an enterprise IDM is
 * configured (Entra/AD/Okta) the search is server-side and query-driven; for
 * demos the local ZTAI users act as the directory and are searched the same way.
 * Selecting a row sets the owner; leaving it unset lets the backend default one.
 */
const IDMUserSelect: React.FC<IDMUserSelectProps> = ({ apiUrl, value, onChange }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<IDMUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [source, setSource] = useState('local');
  const [requiresQuery, setRequiresQuery] = useState(false);
  const debounceRef = useRef<number | null>(null);

  // Debounce the query (300ms) so directory lookups don't fire per keystroke.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listIdmUsers(apiUrl, debounced)
      .then((res) => {
        if (cancelled) return;
        setUsers(res.users);
        setSource(res.source);
        setRequiresQuery(res.requiresQuery);
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiUrl, debounced]);

  const sourceLabel = SOURCE_LABEL[source] || source;
  const needsMoreInput = requiresQuery && debounced.length < 2;

  return (
    <div className="idm-select">
      <div className="mcp-select-searchbar">
        <Icon name="search" size={15} />
        <input
          className="ent-input"
          type="text"
          placeholder={requiresQuery ? `Search ${sourceLabel}…` : t('components.iDMUserSelect.searchUsersByNameOrEmail')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="idm-select-source">
        <Icon name="server" size={11} /> {t('components.idmUserSelect.source')} <strong>{sourceLabel}</strong>
        {loading && <span className="idm-select-loading"> {t('components.iDMUserSelect.searching')}</span>}
      </div>

      {error ? (
        <div className="mcp-select-error" style={{ margin: '8px 2px' }}><Icon name="warning" size={12} /> {error}</div>
      ) : needsMoreInput ? (
        <div className="mcp-select-empty">{t('components.iDMUserSelect.typeAtLeast2CharactersTo', { v0: sourceLabel })}</div>
      ) : (
        <div className="idm-select-list">
          {users.length === 0 ? (
            <div className="mcp-select-empty">{loading ? t('components.iDMUserSelect.searching2') : `No users match “${query}”.`}</div>
          ) : (
            users.map((u) => {
              const active = value === u.user_id;
              return (
                <button
                  type="button"
                  key={u.user_id}
                  className={`idm-select-row ${active ? 'active' : ''}`}
                  onClick={() => onChange(active ? null : u.user_id, active ? null : u)}
                >
                  <span className="idm-select-avatar">{(u.name || u.user_id).charAt(0).toUpperCase()}</span>
                  <span className="idm-select-info">
                    <span className="idm-select-name">{u.name}</span>
                    {u.email && <span className="idm-select-email">{u.email}</span>}
                  </span>
                  {u.role && <span className="code-token muted">{u.role}</span>}
                  {active && <Icon name="check" size={15} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default IDMUserSelect;
